import { XmlParser } from '@/classes/xmlparser'
import Axios from 'axios'
import { IMPORT_TYPES } from '../../classes/library'
import { BATCH_SIZE } from '../../constants'
import { IRekordboxFolderNode, IRekordboxTrack, IRekordboxXml } from '../../interfaces/rekordboxxml'
import { ITraktorCollectionEntry, ITraktorNml, ITraktorNmlLocation, ITraktorNode } from '../../interfaces/traktornml'
import { IVirtualDjXml, IVirtualDjXmlPlaylist, IVirtualDjXmlSong } from '../../interfaces/virtualdjxml'
import store from '../../store'
import { sentryMessage } from '../../system/sentry'

export class ImportError extends Error {}

interface IDvsArgs {
  vdjPlaylists?: IVirtualDjXmlPlaylist[]
  playlistNodesForPartial?: (IRekordboxFolderNode | ITraktorNode)[]
  playlistEntrySet?: Set<string>
  vdjTotalNodes?: number
  isPartialImport: boolean
}

function traktorGetPath(loc: ITraktorNmlLocation): string {
  return `${loc.VOLUME}${loc.DIR}${loc.FILE}`
}

function xmlSanityCheck(xml: string, filename: string, importType: IMPORT_TYPES): void {
  if (filename.toLowerCase().endsWith('.xml') && xml.startsWith(`<VirtualDJ_Database`)) {
    window.alert(`You uploaded the VirtualDJ database.xml file but you should upload the "Database Backup.zip" file that VirtualDJ creates. Please check the wiki page for instructions.`)

    throw new ImportError('user uploaded vdj xml for partial upload but virtualdj cant be uploaded partially')
  }

  if (importType === IMPORT_TYPES.Rekordbox) {
    if (!xml.includes(`PRODUCT Name="rekordbox"`)) {
      window.alert('The library file you uploaded does not appear to be correct. Please try exporting your library from Rekordbox again.')

      throw new ImportError('rekordbox xml contents does not contain header')
    }

    if (xml.includes(`<COLLECTION Entries="0">`) || !xml.includes(`<TRACK `)) {
      window.alert('The library file you uploaded has no tracks in it. Please try exporting your library from Rekordbox again.')

      throw new ImportError('rekordbox xml contents contain no tracks')
    }
  }
  if (importType === IMPORT_TYPES.Traktor) {
    if (!xml.includes(`PROGRAM="Traktor"`)) {
      window.alert('The library file you uploaded does not appear to be correct. Please try exporting your library from Traktor again.')

      throw new ImportError('traktor xml contents does not contain header')
    }

    if (xml.includes(`<COLLECTION ENTRIES="0"`)) {
      window.alert('The library file you uploaded has no tracks in it. Please try exporting your library from Traktor again.')

      throw new ImportError('traktor nml contents contain no tracks')
    }
  } else if (importType === IMPORT_TYPES.VirtualDJ) {
    if (filename.toLowerCase().endsWith('.xml')) {
      window.alert(`You uploaded the VirtualDJ database.xml file but you should upload the "Database Backup.zip" file that VirtualDJ creates. Please check the wiki page for instructions.`)

      throw new ImportError('user uploaded vdj xml but should upload vdj zip')
    }

    if (!xml.startsWith(`<VirtualDJ_Database`)) {
      window.alert('The library file you uploaded does not appear to be correct. Please try exporting your library from VirtualDJ again.')

      throw new ImportError('virtualdj xml contents does not contain header')
    }
  }
}

function getVersion(firstChunk: string, importType: IMPORT_TYPES): string | null {
  let regex: RegExp

  if (importType === IMPORT_TYPES.Rekordbox) {
    regex = / Version="(.*?)" /
  } else if (importType === IMPORT_TYPES.Traktor) {
    regex = / VERSION="(.*?)">/
  } else if (importType === IMPORT_TYPES.VirtualDJ) {
    regex = / Version="(.*?)">/
  } else {
    throw new Error('unsupported importType')
  }

  const versionMatch = firstChunk.match(regex)

  if (versionMatch && versionMatch.length > 1) {
    return versionMatch[1]
  }

  return null
}

export async function parseAndImportLibraryStreaming(filename: string, file: File, importType: IMPORT_TYPES, dvsArgs: IDvsArgs): Promise<void> {
  let readStream = new Response(file).body as ReadableStream

  const libraryParserTracks = new XmlParser(readStream)

  await libraryParserTracks.parseFirstChunk()

  xmlSanityCheck(libraryParserTracks.firstChunk, filename, importType)

  if (importType === IMPORT_TYPES.Rekordbox) {
    libraryParserTracks.setNodeParameters('TRACK', 'COLLECTION', 'Entries')
  } else if (importType === IMPORT_TYPES.Traktor) {
    libraryParserTracks.setNodeParameters('ENTRY', 'COLLECTION', 'ENTRIES')
  } else if (importType === IMPORT_TYPES.VirtualDJ) {
    libraryParserTracks.setNodeParameters('Song', 'VirtualDJ_Database')
  }

  await Axios.post('/library/start-library-import', {
    importType,
    importTypeVersion: getVersion(libraryParserTracks.firstChunk, importType),
    isPartialImport: dvsArgs.isPartialImport
  })

  let totalTracksSent: number = 0
  let totalTracksParsed: number = 0
  let totalNodes: number | null = null
  let batchBuffer: (IRekordboxTrack | ITraktorCollectionEntry | IVirtualDjXmlSong)[] = []
  let hasMoreNodes: boolean = true
  let batchNumber: number = 0
  let rekordboxHighestTrackId: number = -1

  do {
    const tracks: (IRekordboxTrack | ITraktorCollectionEntry | IVirtualDjXmlSong)[] = await libraryParserTracks.getNodeBatch()

    // Must be done after the first batch
    if (totalNodes === null) {
      if (dvsArgs.isPartialImport && dvsArgs.playlistEntrySet) {
        totalNodes = dvsArgs.playlistEntrySet.size
      } else {
        totalNodes = dvsArgs.vdjTotalNodes || libraryParserTracks.totalNodes
      }
    }

    if (dvsArgs.isPartialImport) {
      let addedTracks: (IRekordboxTrack | ITraktorCollectionEntry | IVirtualDjXmlSong)[] = []

      if (importType === IMPORT_TYPES.Rekordbox) {
        addedTracks = tracks.filter(t => (dvsArgs.playlistEntrySet as Set<string>).has((t as IRekordboxTrack).$.TrackID))
      } else if (importType === IMPORT_TYPES.Traktor) {
        addedTracks = tracks.filter(t => (dvsArgs.playlistEntrySet as Set<string>).has(traktorGetPath((t as any).LOCATION[0].$)))
      }

      batchBuffer = batchBuffer.concat(addedTracks)

      totalTracksParsed += addedTracks.length
    } else {
      batchBuffer = tracks

      totalTracksParsed += tracks.length
    }

    hasMoreNodes = totalTracksParsed < totalNodes

    if (!dvsArgs.isPartialImport && importType !== IMPORT_TYPES.Traktor && libraryParserTracks.closed && totalNodes > totalTracksParsed) {
      sentryMessage(`user library contained ${totalTracksParsed} but xml indicated only ${totalNodes} available`)
    }

    if (batchBuffer.length >= BATCH_SIZE || (!hasMoreNodes && batchBuffer.length > 0) || libraryParserTracks.closed) {
      await Axios.post('/library/import-track-batch', {
        tracks: batchBuffer,
        importType,
        batchNumber
      })

      totalTracksSent += batchBuffer.length

      batchBuffer = []
      batchNumber++
    }

    const percentage: number = 100 / (totalNodes / totalTracksSent)

    store.commit('library/setImportProgressPercentage', percentage)
  } while (hasMoreNodes && !libraryParserTracks.closed)

  if (!dvsArgs.isPartialImport && importType !== IMPORT_TYPES.Traktor && totalTracksSent !== totalNodes) {
    sentryMessage(`expected ${totalNodes} tracks to be sent but sent ${totalTracksSent}`)
  }

  let libraryJson: any = null
  if (importType === IMPORT_TYPES.Rekordbox || importType === IMPORT_TYPES.Traktor) {
    readStream = new Response(file).body as ReadableStream

    const libraryParserMisc = new XmlParser(readStream)

    libraryParserMisc.setNodeParameters('', '')
    libraryParserMisc.setExcludedNode('COLLECTION')

    let miscNodes: any = []

    while (!libraryParserMisc.closed) {
      const batch = await libraryParserMisc.getNodeBatch()

      miscNodes = miscNodes.concat(batch)
    }

    libraryJson = {
      [libraryParserMisc.rootNodeName]: miscNodes[0]
    }

    if (dvsArgs.isPartialImport) {
      if (importType === IMPORT_TYPES.Rekordbox) {
        ;(libraryJson as IRekordboxXml).DJ_PLAYLISTS.PLAYLISTS = [
          {
            NODE: dvsArgs.playlistNodesForPartial as IRekordboxFolderNode[]
          }
        ]
      } else if (importType === IMPORT_TYPES.Traktor) {
        ;(libraryJson as ITraktorNml).NML.PLAYLISTS = [
          {
            NODE: dvsArgs.playlistNodesForPartial as ITraktorNode[]
          }
        ]
      }
    }

    if (importType === IMPORT_TYPES.Rekordbox) {
      // We need the highest trackId in order to let rekordcloud add new tracks to a library. Can't reliably check this on the server because partial imports dont have all trackIds
      readStream = new Response(file).body as ReadableStream

      const libraryParserHighestId = new XmlParser(readStream)
      libraryParserHighestId.setNodeParameters('TRACK', 'COLLECTION', 'Entries')

      do {
        const tracks: IRekordboxTrack[] = await libraryParserHighestId.getNodeBatch()

        for (const track of tracks) {
          if (track.$ && (track as IRekordboxTrack).$.TrackID) {
            const trackId: number = parseInt((track as IRekordboxTrack).$.TrackID, 10)

            if (trackId > rekordboxHighestTrackId) rekordboxHighestTrackId = trackId
          } else {
            sentryMessage('rekordbox import: found track without trackId', track)
          }
        }
      } while (!libraryParserHighestId.closed)
    }
  } else if (importType === IMPORT_TYPES.VirtualDJ) {
    libraryJson = {
      VirtualDJ_Database: {
        Playlists: dvsArgs.vdjPlaylists
      }
    } as IVirtualDjXml
  }

  await Axios.post('/library/finish-library-import', {
    library: libraryJson,
    importType,
    rekordboxHighestTrackId
  })

  await store.dispatch('library/fetchMetadata', { force: true }) // Must force new metadata or it has the timestamp of the previous library

  await store.dispatch('library/fetchLibrary')
}

export async function parsePlaylistsXml(filename: string, file: File, importType: IMPORT_TYPES): Promise<any[]> {
  const reader = new FileReader()

  const playlistsXml: string = await new Promise((resolve, reject) => {
    reader.onload = function (event) {
      let xml: string = (event.target as any).result

      try {
        xmlSanityCheck(xml, filename, importType)
      } catch (error) {
        return reject(error)
      }

      const playlistsIndexStart: number = xml.lastIndexOf('<PLAYLISTS>')

      xml = xml.slice(playlistsIndexStart)

      const playlistsIndexEnd: number = xml.lastIndexOf('</PLAYLISTS>')

      xml = xml.slice(0, playlistsIndexEnd + '</PLAYLISTS>'.length)

      resolve(xml)
    }

    reader.readAsText(file)
  })

  const highWaterMark = 32 * 1024
  let skip = 0
  const readStream = new ReadableStream({
    pull(controller) {
      if (skip >= playlistsXml.length) {
        controller.close()
      }

      const chunk: string = playlistsXml.slice(skip, skip + highWaterMark)

      controller.enqueue(new TextEncoder().encode(chunk).buffer)

      skip += highWaterMark
    }
  })

  const libraryParserPlaylists = new XmlParser(readStream)

  await libraryParserPlaylists.parseFirstChunk()

  if (filename.toLowerCase().endsWith('.xml')) {
    importType = IMPORT_TYPES.Rekordbox
  } else if (filename.toLowerCase().endsWith('.nml')) {
    importType = IMPORT_TYPES.Traktor
  }

  if (!importType) {
    throw new ImportError(`could not determine import type from filename: ` + filename)
  }

  libraryParserPlaylists.setNodeParameters('NODE', 'PLAYLISTS')

  let playlistNodes: any[] = []
  while (!libraryParserPlaylists.closed) {
    const nodes = await libraryParserPlaylists.getNodeBatch()

    playlistNodes = playlistNodes.concat(nodes)
  }

  return playlistNodes
}
